import { Box } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import DefaultText from "../../../../components/Text/DefaultText";
import { Product, ProductSizes, Size } from "../../../../gobalType";
import {
  filterProductSizesBySelection,
  findProductSizeForFilter,
} from "../../PlantsPage/utils/filterSelection";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { TypesAction } from "../../../../redux/typesAction";
import { RootState } from "../../../../redux";
import { toast } from "react-toastify";
import { AvailabilitySizeTag } from "./components/AvailabilitySizeTag";
import { QuickAddAction } from "./components/QuickAddAction";
import { PATH_IMAGE_URL } from "../../../../services/api.constant";

/**
 * 1 = plants, 2 = pots (cùng quy ước ProductDetails & Cart).
 * Nhiều list API không gửi `product.category_id` — nếu không suy từ size / trang sẽ rơi nhầm thành pots (plt).
 */
function resolveCategoryIdForCart(
  item: Product | any,
  selectedSize: ProductSizes | undefined,
  listCategoryId?: 1 | 2
): 1 | 2 {
  const p = Number(item?.category_id);
  if (p === 1 || p === 2) return p;
  if (selectedSize) {
    const s = Number(selectedSize.category_id);
    if (s === 1 || s === 2) return s;
  }
  const fromSizes = item?.product_sizes
    ?.map((x: ProductSizes) => Number(x?.category_id))
    .find((c: number) => c === 1 || c === 2);
  if (fromSizes === 1 || fromSizes === 2) return fromSizes;
  if (listCategoryId === 1 || listCategoryId === 2) return listCategoryId;
  return 1;
}

interface IProps {
  item: Product | any;
  selectedSizes?: string[];
  allSize?: Size[];
  /** Trang /products-plants nên truyền 1 khi thiếu category trên item. */
  listCategoryId?: 1 | 2;
}
function ItemSize({ item, selectedSizes = [], allSize = [], listCategoryId }: IProps) {
  const [quantity, setQuantity] = useState(0);
  const isMobile = useMediaQuery("(max-width:600px)", {
    defaultMatches:
      typeof window !== "undefined" ? window.innerWidth <= 600 : false,
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { carts } = useSelector((state: RootState) => state.cart);
  const isSpecialProduct = Number(item?.is_special_product) === 1;

  const currentSize = item?.product_sizes
    ?.filter((size: ProductSizes) => size?.quantity_available?.length)
    .slice(0, 9);

  const handlePlantDetails = (
    product: Product | any,
    product_size: ProductSizes
  ) => {
    navigate(`/product-details/${product.id}`, {
      state: {
        product_id: product_size?.product_id,
        size_id: product_size?.id,
      },
    });
  };

  const hasFilterSize = selectedSizes.length > 0;
  const availableWithQty = item?.product_sizes?.filter(
    (size: ProductSizes) => size?.quantity_available?.length
  );

  const selectedSize = hasFilterSize
    ? findProductSizeForFilter(availableWithQty, selectedSizes, allSize)
    : undefined;

  const productCategoryId = resolveCategoryIdForCart(
    item,
    selectedSize,
    listCategoryId
  );
  const nextItemCategory = productCategoryId === 1 ? "pcs" : "plt";

  /** Chỉ khi đã chọn size cụ thể trong filter; "All sizes" → không hiện Add */
  const shouldShowQuickAdd = hasFilterSize;

  const sizeForDetailNav = (): ProductSizes | undefined =>
    selectedSize ?? availableWithQty?.[0] ?? item?.product_sizes?.[0];

  const handlePlantNameNav = () => {
    const size = sizeForDetailNav();
    if (size) {
      handlePlantDetails(item, size);
      return;
    }
    navigate(`/product-details/${item.id}`, {
      state: { product_id: item?.id },
    });
  };

  const thumbPath = item?.thumbnail?.[0]?.image_path;
  const thumbSrc = thumbPath ? `${PATH_IMAGE_URL}${thumbPath}` : undefined;

  const desktopAvailabilitySizes = hasFilterSize
    ? filterProductSizesBySelection(
        availableWithQty,
        selectedSizes,
        allSize
      ).slice(0, 9)
    : currentSize;

  useEffect(() => {
    setQuantity(0);
  }, [item?.id]);

  const canAddToCart = (newItem: any) => {
    if (carts.length === 0) return true;

    const currentCategory = carts[0].isCategory;

    if (currentCategory !== newItem.isCategory) {
      return false;
    }

    return true;
  };

  const handleAddToCart = () => {
    if (quantity <= 0) {
      toast.error("Please select the product quantity");
      return;
    }
    if (!selectedSize) {
      toast.error("Could not match the selected size to this product.");
      return;
    }
    const price = Number(selectedSize?.user_group_prices?.[0]?.price) || 0;
    const totalProduct =
      Number(selectedSize?.quantity_available?.[0]?.quantity) || 0;

    const product = {
      ...item,
      id: item?.id,
      name: item?.name,
      /** CartTable hiển thị `product_name`; list plants thường chỉ có `name`. */
      product_name: item?.product_name ?? item?.name ?? "",
      sizeName: selectedSize?.name || "",
      size_id: selectedSize?.id || 0,
      product_id: selectedSize?.product_id || item?.id || 0,
      quantity,
      price,
      totalProduct,
      extractID: `${selectedSize?.product_id || item?.id}${selectedSize?.id || 0}`,
      isCategory: productCategoryId === 1 ? "pcs" : "plt",
      thumbnail: item?.thumbnail || [],
    };

    if (!auth?.access_token) {
      navigate("/login");
      return;
    }
    if (!canAddToCart({ isCategory: nextItemCategory })) {
      toast.error("Cannot mix plants and pots in the same cart.");
      return;
    }

    dispatch({
      type: TypesAction.ADD_TO_CART,
      payload: product,
    });
    toast.success("Added product to cart successfully");
    setQuantity(0);
  };

  if (isMobile) {
    return (
      <Box
        className="item item--mobile-flat"
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
        }}>
        <Box
          className="content-products"
          component="button"
          type="button"
          aria-label={`View details: ${item?.name ?? "product"}`}
          onClick={handlePlantNameNav}
          sx={{
            flex: 1,
            minWidth: 0,
            pl: "10px",
            pr: 1,
            m: 0,
            border: "none",
            background: "none",
            textAlign: "left",
            cursor: "pointer",
            WebkitTapHighlightColor: "transparent",
            font: "inherit",
            color: "inherit",
          }}>
          <DefaultText
            style={{
              marginLeft: 0,
              fontWeight: 600,
              color: isSpecialProduct ? "#d32f2f" : "#22292E",
            }}>
            {item?.name}
          </DefaultText>
        </Box>
        {shouldShowQuickAdd && availableWithQty && availableWithQty.length > 0 && (
          <QuickAddAction
            variant="inline"
            quantity={quantity}
            onQuantityChange={setQuantity}
            canAdd={
              Boolean(selectedSize) &&
              quantity > 0 &&
              canAddToCart({ isCategory: nextItemCategory })
            }
            onAdd={handleAddToCart}
          />
        )}
      </Box>
    );
  }

  return (
    <Box className="item">
      <Box
        sx={{ width: { xs: "100%", sm: "39%" }, minWidth: 0 }}
        className="content-products">
        {thumbSrc ? (
          <Box
            component="img"
            src={thumbSrc}
            alt=""
            onClick={handlePlantNameNav}
            sx={{
              width: { sm: 56 },
              height: { sm: 68 },
              objectFit: "cover",
              flexShrink: 0,
              ml: { xs: "10px", sm: "15px" },
              mr: 1.5,
              borderRadius: "4px",
              cursor: "pointer",
              display: { xs: "none", sm: "block" },
            }}
          />
        ) : null}
        <Box
          component="button"
          type="button"
          aria-label={`View details: ${item?.name ?? "product"}`}
          onClick={handlePlantNameNav}
          sx={{
            m: 0,
            p: 0,
            border: "none",
            background: "none",
            textAlign: "left",
            cursor: "pointer",
            font: "inherit",
            color: "inherit",
            minWidth: 0,
            flex: 1,
          }}>
          <DefaultText
            style={{
              marginLeft: thumbSrc ? 0 : { xs: "10px", sm: "15px" },
              fontWeight: { xs: 600, sm: 500 },
              color: isSpecialProduct ? "#d32f2f" : "#22292E",
            }}>
            {item?.name}
          </DefaultText>
        </Box>
      </Box>
      <Box
        className="size-products"
        sx={{
          minHeight: { xs: "40px", sm: "75px" },
          maxHeight: "none",
          py: 1,
        }}>
        <Box
          className="list-sizes"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            alignItems: "center",
            width: "100%",
          }}>
          {desktopAvailabilitySizes?.map((size: ProductSizes, index: number) => (
            <Box
              key={size?.id ?? `sz-${index}`}
              className="size-detail"
              sx={{
                flex: "0 1 auto",
                maxWidth: "100%",
                minWidth: 0,
              }}>
              <AvailabilitySizeTag
                product={item}
                size={size}
                onClick={handlePlantDetails}
              />
            </Box>
          ))}
        </Box>
      </Box>
      {shouldShowQuickAdd && (
        <QuickAddAction
          quantity={quantity}
          onQuantityChange={setQuantity}
          canAdd={
            Boolean(selectedSize) &&
            quantity > 0 &&
            canAddToCart({ isCategory: nextItemCategory })
          }
          onAdd={handleAddToCart}
        />
      )}
    </Box>
  );
}

export default ItemSize;
